// cleanup_convert_to_glu_component_USER_EMULATION
// cleanup_convert_to_typescript_file_USER_EMULATION
// cleanup_hoc_to_hooks_USER_EMULATION
// cleanup_remove_external_libraries_USER_EMULATION
// cleanup_strings_USER_EMULATION

import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { template } from 'lodash';

import Alert from '@glu/alerts-react';
import { Button, TERTIARY } from '@glu/buttons-react';
import { Dropdown } from '@glu/dropdown-react';
import { ExclamationCircleIcon } from '@glu/icons-react';
import { debounce } from '@glu/utilities-react';
import { withFormProvider } from '@glu/validation-react';

import { getCustomersList, getUsersList } from 'api/emulation/service';
import GluButton from 'components/gluValidation/Button';
import useLocale from 'components/useLocale';
import { ALERTVARIATIONS, COMMON_NOT_PERMITTED, WORKSPACES } from 'constants.js';
import { logout as logoutAction, emulate } from 'logic/store/authentication/actions';
import { clearGrids } from 'logic/store/grids/actions';

import { FILTER_DEBOUNCE_TIME, FILTER_MIN_CHARACTERS } from './UserEmulation.constants';
import useStyles from './UserEmulation.styles';
import { buildCustomers, buildUsers } from './UserEmulation.utils';

const emptyArray = [];

const filterDebounce = debounce((filter) => filter(), FILTER_DEBOUNCE_TIME);

const customFilter = (options, filterString) => {
  if (filterString.length < FILTER_MIN_CHARACTERS) {
    return options;
  }

  const sanitized = filterString.replace(/[#-.]|[[-^]|[?|{}]/g, '\\$&');
  const query = new RegExp(sanitized, 'i');

  return options.filter(({ name }) => name.match(query));
};

export const UserEmulationComponent = ({
  data: formState,
  logout,
  onChange: formChange,
  startEmulation,
  user,
  validate: onValidate
}) => {
  // Validation state
  const [validations, setValidations] = useState({
    customer: false,
    user: false
  });

  // selected values
  const [values, setValues] = useState({
    customer: {},
    user: {}
  });

  const [permitted, setPermitted] = useState(true);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [disableUsers, setDisableUsers] = useState(true);
  const classes = useStyles();
  const history = useHistory();
  const locale = useLocale();
  const validationValues = Object.values(validations);
  const valid = validationValues.filter(Boolean);
  const formValid = valid.length === validationValues.length;

  useEffect(() => {
    let subscription;

    if (!customerOptions.length && permitted) {
      subscription = getCustomersList().subscribe((customer) => {
        const { code } = customer?.[0] || {};
        const notPermitted = code === COMMON_NOT_PERMITTED;
        if (notPermitted) {
          setPermitted(false);
        } else {
          const newList = buildCustomers(customer);
          if (newList.length) {
            setCustomerOptions(newList);
            setDisableUsers(false);
          } else {
            setDisableUsers(true);
          }
        }
      });
    }

    return () => subscription?.unsubscribe();
  }, [customerOptions, permitted]);

  useEffect(() => {
    let subscription;
    if (values.customer.code) {
      subscription = getUsersList(values.customer.code).subscribe((user) => {
        setUserOptions(buildUsers(user));
        setDisableUsers(false);
      });
    }

    return () => subscription?.unsubscribe();
  }, [values.customer]);

  /**
   * Performs an update of value, when any of fields is changed
   *
   * @param {string} name
   * @param {string|number} value
   * @returns {void}
   */
  const onChange = (options) => (name, value) => {
    const valueId = value?.[0]?.id;
    if (!valueId || formState[name] === valueId) {
      return;
    }

    setValidations((valid) => ({
      ...valid,
      [name]: true
    }));

    setValues((values) => ({
      ...values,
      [name]: options.find((x) => x.id === valueId) || null
    }));

    // user need to be cleared, when customer is changed
    if (name === 'customer') {
      formChange('user', null);
    }
    formChange(name, valueId);
  };

  /**
   * Performs a submission of the form, when submit button is clicked
   *
   * @param {SyntheticEvent} event
   * @returns {void}
   */
  const onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    startEmulation({
      userCode: values.user.code,
      userName: values.user.value,
      customerCode: values.customer.code,
      customerName: values.customer.value
    });
  };

  if (user.emulationStarted) {
    return history.push({ pathname: WORKSPACES.PYMTCNTR });
  }

  return (
    <section className={classes.root}>
      <Alert
        className={classnames(classes.alert, classes.preventOverflow)}
        clsChild={classes.alertChild}
        clsContent
        dataQa="warning-alert"
        dismissible={false}
        suppressIcon
        variant={ALERTVARIATIONS.WARNING}
      >
        {!permitted && (
          <Alert
            className={classes.emulationAlert}
            dataQa="customer-emulation-alert"
            message={
              <>
                <p>{locale['userEmulation.noPermission']}</p>
                <p>{locale['userEmulation.checkPermission']}</p>
              </>
            }
            variant={ALERTVARIATIONS.ERROR}
          />
        )}

        <ExclamationCircleIcon className={classes.icon} />
        <h2 className={classes.h2}>{locale['userEmulation.title']}</h2>
        <h4 className={classes.h4}>{locale['userEmulation.description']}</h4>

        <form className={classes.form} onSubmit={onSubmit}>
          <fieldset className={classes.fieldset}>
            <Dropdown
              className={classnames('form-group', classes.dropDown)}
              dataQa="customer"
              defaultSelectMessage={locale['common.select']}
              disabled={customerOptions.length === 0}
              disableMultiButton
              filter
              filterDebounce={filterDebounce}
              filterFunction={customFilter}
              filterPlaceholder={template(locale['userEmulation.selectCustomer.enterNCharacters'])(
                { length: FILTER_MIN_CHARACTERS }
              )}
              htmlId="customer"
              labelOnTop
              labelText={locale['userEmulation.selectCustomer.label']}
              matchBy={emptyArray}
              name="customer"
              onChange={onChange(customerOptions)}
              options={customerOptions}
              showTooltip={false}
            />

            <Dropdown
              key={JSON.stringify(userOptions)}
              className={classnames('form-group', classes.dropDown)}
              dataQa="user"
              defaultSelectMessage={locale['common.select']}
              disabled={customerOptions.length === 0 || (!values.customer.code && !disableUsers)}
              disableMultiButton
              filter
              htmlId="user"
              labelOnTop
              labelText={`${locale['common.select']} user`}
              name="user"
              onChange={onChange(userOptions)}
              options={userOptions}
              showTooltip={false}
            />
          </fieldset>

          <footer className={classes.buttons}>
            <GluButton
              className="btn btn-primary"
              disabled={!formValid}
              name={locale['common.submit']}
              onFocus={() => onValidate()}
              type="submit"
            >
              {locale['userEmulation.start']}
            </GluButton>

            <Button
              className="btn tertiary cancel"
              onClick={(...params) => {
                clearGrids();
                return logout(...params);
              }}
              variant={TERTIARY}
            >
              {locale['common.cancel']}
            </Button>
          </footer>
        </form>
      </Alert>
    </section>
  );
};

UserEmulationComponent.propTypes = {
  data: PropTypes.shape({}),
  onChange: PropTypes.func.isRequired,
  validate: PropTypes.func,
  logout: PropTypes.func.isRequired,
  startEmulation: PropTypes.func.isRequired,
  user: PropTypes.shape({
    emulationStarted: PropTypes.bool.isRequired
  }).isRequired
};

UserEmulationComponent.defaultProps = {
  data: {},
  validate: null
};

const Validations = withFormProvider(UserEmulationComponent);

/* istanbul ignore next */
const mapStateToProps = (state) => ({
  user: state?.auth?.user ?? null
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => ({
  logout: (...params) => dispatch(logoutAction(...params)),
  startEmulation: (...params) => dispatch(emulate(...params)),
  clearGrids: () => clearGrids(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Validations);
